import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "chart" }

import { Doughnut } from 'vue-chartjs';
import { ChartConfig } from '@/features/reports/components/chartjs/chartConfig';
import { Data, LabelsAndData } from '@/features/reports/models';
import ColourService from '@/shared/services/color';
import {
  DoughnutChartLabel,
  LabelProvider,
  ResizeStrategy,
} from '@/features/reports/components/chartjs/plugins/doughnutLabels/types';
import { chartDataUtil } from '@/features/reports/util/ChartDataUtil';
import HtmlFormatter from '@/shared/services/htmlFormatter';
import { computed, nextTick, onMounted, PropType, ref } from 'vue';
import type { ChartData, TooltipModel } from 'chart.js';
import { TooltipItem } from 'chart.js/dist/types';
import merge from 'lodash.merge';


export default /*@__PURE__*/_defineComponent({
  __name: 'DoughnutChartBaseWrapper',
  props: {
  data: {
    type: Object as PropType<Data>,
  },
  labelsAndData: {
    type: Object as PropType<LabelsAndData>,
  },
  title: {
    type: String,
    required: true,
  },
  legendElementId: {
    type: String,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const chartRef = ref<InstanceType<typeof Doughnut> | null>(null);

onMounted(() => {
  if (chartRef.value) {
    nextTick(() => createLegend());
  }
});

const mapData = computed<ChartData<'doughnut'>>(() => {
  let labels: string[] = [];
  let data: number[] = [];

  if (props.data) {
    labels = props.data.scale.labels;
    data = props.data.series[0].data; // only support a single series
  } else if (props.labelsAndData) {
    labels = props.labelsAndData.labels;
    data = props.labelsAndData.data;
  } else {
    return {
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: [],
          borderColor: [],
          hoverBackgroundColor: [],
          hoverBorderColor: [],
          borderWidth: 3,
        },
      ],
    };
  }

  // assumes that any grouping into "others" and ordering has already been done
  const coloursRequired = labels.length;
  const backgroundPalette = ChartConfig.backgroundColoursForRandomItems(coloursRequired);
  const borderPalette = ChartConfig.singleColourPalette(coloursRequired, '#ffffff');

  return {
    labels,
    datasets: [
      {
        data,
        backgroundColor: backgroundPalette,
        hoverBackgroundColor: ColourService.setAlphas(backgroundPalette, 0.6),
        borderColor: borderPalette,
        hoverBorderColor: borderPalette,
        borderWidth: 3,
      },
    ],
  };
});

const chartOptions = computed(() => {
  const overrides = {
    cutout: '73%', // Replaced cutoutPercentage
    plugins: {
      inlineLabels: false,
      crosshair: false,
      tooltip: {
        enabled: false, // Custom tooltip handling via events
      },
      doughnutlabel: {
        labelProvider,
        resizeStrategy: ResizeStrategy.TRUNCATE,
      },
      legend: {
        display: false,
        onHover: (event, legendItem, legend) => {
          const chart = legend.chart;
          const index = legendItem.index;
          if (index !== undefined) {
            highlightActiveSegment(index, true);
            chart.options.plugins?.tooltip?.custom?.(createTooltipEventObject(chart, index));
          }
        },
        onLeave: (event, legendItem, legend) => {
          const chart = legend.chart;
          const index = legendItem.index;
          if (index !== undefined) {
            highlightActiveSegment(index, false);
            chart.options.plugins?.tooltip?.custom?.({ opacity: 0 } as TooltipModel<'doughnut'>);
          }
        },
      },
    },
  };

  return merge({}, ChartConfig.globalOptions, overrides);
});

function getHoverTooltipWhenNoSelection(): DoughnutChartLabel[] {
  return [
    { text: String(total.value), font: { lineHeight: 40, size: 40, family: '"Quicksand", sans-serif' } },
    { text: props.title, font: { lineHeight: 12, size: 12, family: '"Quicksand", sans-serif' } },
  ];
}

function labelProvider(): LabelProvider {
  return (chart, index, key, value) => {
    if (value === undefined || !key) {
      return getHoverTooltipWhenNoSelection();
    } else {
      return getHoverTooltipWhenSelection(String(key), value);
    }
  };
}

function getHoverTooltipWhenSelection(legendItemName: string, value: number): DoughnutChartLabel[] {
  const percentageOfTotal = (value / total.value) * 100;
  const roundedPercentage = Math.round(percentageOfTotal * 100) / 100;

  return [
    { text: `${roundedPercentage}%`, font: { lineHeight: 40, size: 40, family: '"Quicksand", sans-serif' } },
    { text: legendItemName, font: { lineHeight: 12, size: 12, family: '"Quicksand", sans-serif' } },
  ];
}

const total = computed(() => chartDataUtil.total(mapData.value.datasets![0].data as number[]));

function createLegend() {
  if (!chartRef.value.chart) {
    return;
  }

  const chart = chartRef.value.chart;
  const legendContainer = document.getElementById(props.legendElementId);

  if (!legendContainer) {
    console.error(`Legend container with ID ${props.legendElementId} not found`);
    return;
  }

  const legendItems = chart.options.plugins?.legend?.labels?.generateLabels?.(chart) || [];
  const dataSets = chart.data.datasets![0];
  const backgroundColours = dataSets.backgroundColor as string[];

  const text = [];
  text.push(`<table class="chartjs-custom-legend"><colgroup><col/><col width="100%"/><col width="0%"/></colgroup>`);

  for (let i = 0; i < legendItems.length; i++) {
    if (legendItems[i].text) {
      const originalLabel = legendItems[i].text || '';
      const encodedLabel = HtmlFormatter.escapeHtmlCharacters(originalLabel);
      const legendColour = i >= backgroundColours.length ? '#e5e5e5' : backgroundColours[i];
      text.push(`<tr>`);
      text.push(`<td>`);
      text.push(`<div style="width: 8px; background-color:${legendColour}"> </div>`);
      text.push('</td>');
      text.push(`<td class="legend-label" title="${encodedLabel}">${encodedLabel}</td>`);
      text.push(`<td class="legend-value">${dataSets.data![i]}</td>`);
      text.push('</tr>');
    }
  }
  text.push('</table>');
  legendContainer.innerHTML = text.join('');

  if (total.value > 0) {
    const legendRows = legendContainer.getElementsByTagName('tr');
    let index = 0;
    for (const row of legendRows) {
      const hoveredDatasetIndex = index++;
      row.addEventListener('mouseover', () => {
        row.style.backgroundColor = '#e5e5e5';
        highlightActiveSegment(hoveredDatasetIndex, true);
        chart.options.plugins?.tooltip?.external?.({
          chart,
          tooltip: createTooltipEventObject(chart, hoveredDatasetIndex),
        });
        chart.update();
      });
      row.addEventListener('mouseout', () => {
        row.style.backgroundColor = '';
        highlightActiveSegment(hoveredDatasetIndex, false);
        chart.options.plugins?.tooltip?.external?.({
          chart,
          tooltip: { opacity: 0 } as TooltipModel<'doughnut'>,
        });
        chart.update();
      });
    }
  }
}

function createTooltipEventObject(
  chart: InstanceType<typeof Doughnut>['chart'],
  hoveredDatasetIndex: number,
): TooltipModel<'doughnut'> {
  const legendItems = chart.options.plugins?.legend?.labels?.generateLabels?.(chart) || [];
  const dataSets = chart.data.datasets![0];

  const partialTooltip: Partial<TooltipModel<'doughnut'>> = {
    opacity: 1,
    body: [
      {
        lines: [legendItems[hoveredDatasetIndex].text + ': ' + String(dataSets.data![hoveredDatasetIndex])],
        before: [],
        after: [],
      },
    ],
    dataPoints: [
      {
        datasetIndex: 0,
        label: legendItems[hoveredDatasetIndex].text,
        dataIndex: hoveredDatasetIndex,
        formattedValue: dataSets.data![hoveredDatasetIndex],
      } as TooltipItem<'doughnut'>,
    ],
  };

  return partialTooltip as TooltipModel<'doughnut'>;
}

function highlightActiveSegment(segmentIndex: number, highlight: boolean) {
  if (chartRef.value && chartRef.value.chart) {
    const chart = chartRef.value.chart;
    chart.setActiveElements(highlight ? [{ datasetIndex: 0, index: segmentIndex }] : []);
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Doughnut), {
      ref_key: "chartRef",
      ref: chartRef,
      options: chartOptions.value,
      data: mapData.value
    }, null, 8, ["options", "data"])
  ]))
}
}

})